import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { CompanyService } from '../../services/CompanyService/company';
import { ApiEnvelope } from '../../models/auth.dto';
import { MeCompanyResponse, RegisterViaPortalRequest } from '../../models/company.dto';
import { CountrySelect } from '../../shared/components/country-select/country-select';

/**
 * `confirm-account` is the registered-company shortcut: the backend's
 * {@code CompanyService#registerViaPortal} "already registered" branch never reads the company
 * fields on the request (name, registrationNo, countryCode, contactEmail, address) — it only adds
 * a COMPANY_USER to the existing company — so this step collects just the one thing that
 * *is* used, the admin's full name, instead of the full `details` form.
 */
type Step = 'credentials' | 'pick-company' | 'details' | 'confirm-account' | 'success';

@Component({
  selector: 'app-register-via-portal',
  imports: [CommonModule, FormsModule, RouterLink, CountrySelect],
  templateUrl: './register-via-portal.html',
  styleUrl: './register-via-portal.scss',
})
export class RegisterViaPortal {
  step = signal<Step>('credentials');
  isSubmitting = signal(false);
  errorMessage = signal('');
  adminEmailError = signal('');
  successMessage = signal('');
  showPassword = signal(false);

  credentials = { email: '', password: '' };

  companies = signal<MeCompanyResponse[]>([]);
  selectedCompany = signal<MeCompanyResponse | null>(null);

  // middlewareCompanyId/admin.email/admin.password are filled in on selectCompany() — the latter
  // two stay locked to whatever was just proven against the middleware in step 1, since the
  // backend re-verifies login with exactly what's submitted here, not what was typed in step 1.
  form: RegisterViaPortalRequest = this.blankForm();

  constructor(private companyService: CompanyService) {}

  togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  submitCredentials(): void {
    this.errorMessage.set('');
    this.isSubmitting.set(true);

    this.companyService.middlewareCompanies(this.credentials).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        this.companies.set(res.data);
        this.step.set('pick-company');
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        this.errorMessage.set(
          this.resolveError(err, 'Could not verify your itrace account. Please try again.').message
        );
      },
    });
  }

  /**
   * A company already registered here skips straight to {@code confirm-account} — collecting
   * just the admin's full name — instead of the full {@code details} form, since the backend's
   * "already registered" branch never reads the company fields on the request. A brand-new
   * company still goes through {@code details} to collect them.
   */
  selectCompany(company: MeCompanyResponse): void {
    this.selectedCompany.set(company);
    this.errorMessage.set('');

    if (company.registered) {
      this.form = this.formForExistingCompany(company);
      this.step.set('confirm-account');
      return;
    }

    this.form = {
      middlewareCompanyId: company.id,
      name: company.companyName,
      registrationNo: '',
      countryCode: '',
      contactEmail: this.credentials.email,
      contactPhone: '',
      address: '',
      admin: {
        email: this.credentials.email,
        password: this.credentials.password,
        fullName: '',
      },
    };
    this.step.set('details');
  }

  backToCredentials(): void {
    this.step.set('credentials');
    this.errorMessage.set('');
  }

  backToCompanyList(): void {
    this.step.set('pick-company');
    this.errorMessage.set('');
    this.adminEmailError.set('');
  }

  /** Submits {@link form}, built by either the `details` or the `confirm-account` step. */
  submitRegistration(): void {
    this.errorMessage.set('');
    this.adminEmailError.set('');
    this.isSubmitting.set(true);

    this.companyService.registerViaPortal(this.form).subscribe({
      next: (res) => {
        this.isSubmitting.set(false);
        this.successMessage.set(res.data.message);
        this.step.set('success');
      },
      error: (err: HttpErrorResponse) => {
        this.isSubmitting.set(false);
        const { message, code } = this.resolveError(err, 'Registration failed. Please try again.');
        if (code === 'EMAIL_TAKEN') {
          this.adminEmailError.set(message);
        } else if (code === 'COMPANY_ADMIN_REQUIRED' || code === 'MIDDLEWARE_COMPANY_NOT_FOUND') {
          // Only a company admin (role STA on the middleware) may bring a brand-new company onto
          // the platform, and a company the middleware no longer lists can't be registered either —
          // both send the user back to pick a different company rather than leaving them stuck on
          // a form that can never be submitted.
          this.step.set('pick-company');
          this.errorMessage.set(message);
        } else {
          this.errorMessage.set(message);
        }
      },
    });
  }

  /**
   * Turns a raw HTTP failure into a message this page actually wants to show.
   *
   * <p>The backend's own {@code message} is already safe to display (internal exception text is
   * logged server-side, never returned — see {@code MiddlewareService}), but it is written for any
   * caller of these endpoints, not this specific 3-step wizard. Known codes get a wording tailored
   * to what the user just did here; an unrecognised code still falls back to the backend's own
   * message rather than the generic default, so a server-added error code is never silently
   * replaced with something less specific. A connection failure (`status === 0`, no JSON body at
   * all — the request never reached the server) is detected separately, since there is no
   * `ApiEnvelope` to read a message from in that case.
   */
  private resolveError(err: HttpErrorResponse, fallback: string): { message: string; code?: string } {
    if (err.status === 0) {
      return { message: 'Could not reach the server. Please check your connection and try again.' };
    }

    const body = err.error as ApiEnvelope<unknown> | undefined;
    if (!body || typeof body.message !== 'string') {
      return { message: fallback };
    }

    switch (body.code) {
      case 'VALIDATION_FAILED':
        return {
          message: body.errors ? Object.values(body.errors).join(' ') : body.message,
          code: body.code,
        };
      case 'MIDDLEWARE_AUTH_FAILED':
        return {
          message: 'Incorrect itrace email or password. Please check your credentials and try again.',
          code: body.code,
        };
      case 'MIDDLEWARE_UNAVAILABLE':
      case 'MIDDLEWARE_NOT_CONFIGURED':
        return {
          message: 'The itrace middleware is temporarily unavailable. Please try again in a few minutes.',
          code: body.code,
        };
      case 'MIDDLEWARE_COMPANY_NOT_FOUND':
        return {
          message: 'That company is no longer available on your itrace account. Please pick another.',
          code: body.code,
        };
      default:
        return { message: body.message, code: body.code };
    }
  }

  /**
   * The request for an already-registered company: `name`/`registrationNo`/`countryCode`/
   * `address` are never read by the backend on this branch (it only adds a COMPANY_USER to the
   * existing row), so these are just placeholders satisfying the request's own validation —
   * `admin.fullName` is the only field here that is actually used, and it is left for the person
   * to fill in on the `confirm-account` step.
   */
  private formForExistingCompany(company: MeCompanyResponse): RegisterViaPortalRequest {
    return {
      middlewareCompanyId: company.id,
      name: company.companyName || company.slug,
      registrationNo: company.slug || String(company.id),
      countryCode: 'MY',
      contactEmail: this.credentials.email,
      contactPhone: '',
      address: '',
      admin: {
        email: this.credentials.email,
        password: this.credentials.password,
        fullName: '',
      },
    };
  }

  private blankForm(): RegisterViaPortalRequest {
    return {
      middlewareCompanyId: 0,
      name: '',
      registrationNo: '',
      countryCode: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      admin: { email: '', password: '', fullName: '' },
    };
  }
}
